'use client';

import { deleteLineItem } from '@lib/data/cart';
import { cn } from '@lib/util/cn';
import { Button } from '@modules/common/components/button';
import { CART_CHANGED_EVENT } from '@modules/common/components/cart-provider/constants';
import { Trash } from '@modules/common/icons';
import { useState } from 'react';

const REMOVE_FAILED_MESSAGE = 'Could not remove item. Please try again.';

// `onError` shadows the button's native error handler on purpose — this
// component never needs the DOM one.
type DeleteButtonProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'onError'
> & {
  id: string;
  onDelete?: () => void;
  /**
   * Called when the removal fails. Consumers surface the message their own way
   * (inline `ErrorMessage`, toast). Without a handler the button renders the
   * message itself so the failure is never silent.
   */
  onError?: (message: string) => void;
};

const DeleteButton = ({
  id,
  children,
  className,
  onDelete,
  onError,
  ...buttonProps
}: DeleteButtonProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reportError = (message: string) => {
    if (onError) {
      onError(message);
    } else {
      setError(message);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    setError(null);
    try {
      const result = await deleteLineItem(id);
      if (result?.error) {
        reportError(result.error);
      } else {
        window.dispatchEvent(new Event('medusa:cartitemremoved'));
        window.dispatchEvent(new Event(CART_CHANGED_EVENT));
        onDelete?.();
      }
    } catch (_err) {
      reportError(REMOVE_FAILED_MESSAGE);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between nw-typography-paragraph-xs',
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        aria-label={children ? undefined : 'Remove item'}
        isLoading={isDeleting}
        className="h-auto py-0 px-0 gap-x-1 text-ui-fg-base hover:text-ui-fg-subtle hover:bg-transparent disabled:cursor-wait disabled:opacity-100"
        onClick={() => handleDelete(id)}
        {...buttonProps}
      >
        <Trash aria-hidden="true" />
        <span>{children}</span>
      </Button>
      {error && (
        <span role="alert" className="pl-2 text-fg-danger-strong">
          {error}
        </span>
      )}
      <span role="status" aria-live="polite" className="sr-only">
        {isDeleting ? 'Removing item…' : ''}
      </span>
    </div>
  );
};

export default DeleteButton;
